import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef AddReplyResponse
 * @type {Object}
 * @property {bool} authorIsDeveloper
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} editedAt
 * @property {string} id
 * @property {bool} isEditable
 * @property {string} reviewId
 * @property {string} title
 * @property {string} text
 */

/**
 * Adds a reply to a review
 * @param {object} params - an object of params
 * @param {string} params.accessToken - IMS access token
 * @param {string} params.reviewId - ID of the review for which the reply is added
 * @param {string} params.authorGuid - GUID of the user reviewing leaving the reply
 * @param {string} params.authorName - Name of the user reviewing leaving the reply
 * @param {string} params.title - Title of the reply
 * @param {string} params.text - Text of the reply
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<AddReplyResponse>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['accessToken', 'reviewId', 'authorGuid', 'authorName', 'text'];
  const paramsRules = {
    accessToken: { allowedTypes: ['string'] },
    reviewId: { allowedTypes: ['string'] },
    authorGuid: { allowedTypes: ['string'] },
    authorName: { allowedTypes: ['string'] },
    title: { allowedTypes: ['string'] },
    text: { allowedTypes: ['string'] },
  };

  if (!reviewSettings.endpoint['post-reply']) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR add-reply endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { accessToken, reviewId, authorGuid, authorName, title, text } = params;
  const requestUrl = reviewSettings.endpoint['post-reply'].replace('{reviewId}', reviewId);
  const response = await fetch(requestUrl, {
    signal,
    method: 'POST',
    headers: {
      accept: 'application/vnd.adobe-review.review-reply-v1+json',
      authorization: accessToken,
      'Content-Type': 'application/vnd.adobe-review.review-reply-request-v1+json',
      'x-api-key': reviewSettings.apiKey,
    },
    body: JSON.stringify({
      authorGuid,
      authorName,
      title,
      text,
    }),
  });
  /**
   * @type {AddReplyResponse}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
